import type Game from '../game';
import type { CutsceneStep } from '@kaetram/common/types/messages/outgoing';

export default class CutsceneController {
    private overlay = document.querySelector<HTMLElement>('#cinematic-overlay')!;
    private letterboxTop = document.querySelector<HTMLElement>('#cinematic-letterbox-top')!;
    private letterboxBottom = document.querySelector<HTMLElement>('#cinematic-letterbox-bottom')!;
    private fade = document.querySelector<HTMLElement>('#cinematic-fade')!;
    private title = document.querySelector<HTMLElement>('#cinematic-title')!;
    private subtitle = document.querySelector<HTMLElement>('#cinematic-subtitle')!;
    private skip = document.querySelector<HTMLElement>('#cinematic-skip')!;
    private image = document.querySelector<HTMLImageElement>('#cinematic-image')!;

    private playing = false;
    private steps: CutsceneStep[] = [];
    private stepIndex = 0;
    private skipHandler?: (e: KeyboardEvent) => void;

    public constructor(private game: Game) {}

    public play(steps: CutsceneStep[]): void {
        if (this.playing) return;

        this.playing = true;
        this.steps = steps;
        this.stepIndex = 0;

        this.overlay.classList.add('active');

        this.skipHandler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') this.skipCutscene();
        };
        document.addEventListener('keydown', this.skipHandler);

        setTimeout(() => this.skip.classList.add('visible'), 1500);

        this.runNextStep();
    }

    private runNextStep(): void {
        if (!this.playing || this.stepIndex >= this.steps.length) {
            this.end();
            return;
        }

        let step = this.steps[this.stepIndex++];

        switch (step.type) {
            case 'lockInput': {
                this.game.player.disableAction = true;
                this.game.player.frozen = true;
                this.runNextStep();
                return;
            }

            case 'unlockInput': {
                this.game.player.disableAction = false;
                this.game.player.frozen = false;
                this.runNextStep();
                return;
            }

            case 'fade': {
                let duration = step.duration || 1000;
                this.fade.style.transition = `opacity ${duration}ms ease-in-out`;

                if (step.direction === 'out') this.fade.classList.add('black');
                else this.fade.classList.remove('black');

                setTimeout(() => this.runNextStep(), duration);
                return;
            }

            case 'letterbox': {
                let duration = step.duration || 600;
                this.letterboxTop.style.transition = `height ${duration}ms ease-in-out`;
                this.letterboxBottom.style.transition = `height ${duration}ms ease-in-out`;

                if (step.show) {
                    this.letterboxTop.classList.add('show');
                    this.letterboxBottom.classList.add('show');
                } else {
                    this.letterboxTop.classList.remove('show');
                    this.letterboxBottom.classList.remove('show');
                }

                setTimeout(() => this.runNextStep(), duration);
                return;
            }

            case 'text': {
                let duration = step.duration || 2000,
                    element = step.style === 'subtitle' ? this.subtitle : this.title;

                element.textContent = step.text || '';
                element.classList.add('visible');

                setTimeout(() => {
                    element.classList.remove('visible');
                    setTimeout(() => this.runNextStep(), 400);
                }, duration);
                return;
            }

            case 'music': {
                this.game.audio.playMusic(step.song);
                this.runNextStep();
                return;
            }

            case 'wait': {
                setTimeout(() => this.runNextStep(), step.duration || 1000);
                return;
            }

            case 'overlay': {
                this.game.renderer.updateDarkMask(step.colour || 'rgba(0,0,0,0.8)');
                this.runNextStep();
                return;
            }

            case 'overlayRemove': {
                this.game.overlays.update();
                this.runNextStep();
                return;
            }

            case 'camera': {
                let duration = step.duration || 2000;
                this.game.camera.panTo(step.x || 0, step.y || 0, duration);
                setTimeout(() => this.runNextStep(), duration);
                return;
            }

            case 'cameraReset': {
                this.game.camera.center();
                this.runNextStep();
                return;
            }

            case 'image': {
                let duration = step.duration || 3000;

                if (step.show === false) {
                    this.image.classList.remove('visible');
                    setTimeout(() => {
                        this.image.src = '';
                        this.runNextStep();
                    }, 400);
                    return;
                }

                this.image.src = step.src || '';
                requestAnimationFrame(() => {
                    this.image.classList.add('visible');
                });

                setTimeout(() => this.runNextStep(), duration);
                return;
            }

            default: {
                this.runNextStep();
            }
        }
    }

    public skipCutscene(): void {
        if (!this.playing) return;

        this.end();
    }

    private end(): void {
        this.playing = false;
        this.steps = [];
        this.stepIndex = 0;

        this.game.player.disableAction = false;
        this.game.player.frozen = false;

        this.fade.classList.remove('black');
        this.fade.style.transition = 'opacity 0.3s ease-in-out';
        this.letterboxTop.classList.remove('show');
        this.letterboxBottom.classList.remove('show');
        this.title.classList.remove('visible');
        this.title.textContent = '';
        this.subtitle.classList.remove('visible');
        this.subtitle.textContent = '';
        this.image.classList.remove('visible');
        this.image.src = '';
        this.skip.classList.remove('visible');

        setTimeout(() => {
            this.overlay.classList.remove('active');
        }, 600);

        if (this.skipHandler) {
            document.removeEventListener('keydown', this.skipHandler);
            this.skipHandler = undefined;
        }
    }

    public isPlaying(): boolean {
        return this.playing;
    }
}
